import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate } from "react-router-dom";
import { LayoutDashboard, PhoneCall, MessagesSquare, Users, Building2, Settings, LogOut, Loader2, BookOpen, Headset, CalendarDays, MessageCircleQuestion } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ChangePassword from "@/components/ChangePassword";
import { BrandLogo } from "@/components/BrandLogo";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import LiveScript from "@/pages/LiveScript";
import Objections from "@/pages/Objections";
import Leads from "@/pages/Leads";
import Kennisbank from "@/pages/Kennisbank";
import Beheer from "@/pages/Beheer";
import Belomgeving from "@/pages/Belomgeving";
import Agenda from "@/pages/Agenda";
import { LiveCallAlert } from "@/components/LiveCallAlert";
import { FeedbackBadge } from "@/components/FeedbackBadge";
import { CallQueueBadge } from "@/components/CallQueue";
import { NewLeadsProvider, NewLeadBell } from "@/components/NewLeadAlerts";
import Vragen from "@/pages/Vragen";

const NAV = [
  { to: "/", label: "Overzicht", short: "Home", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/belomgeving", label: "Belomgeving", short: "Bellen", icon: Headset, testid: "nav-belomgeving", queue: true },
  { to: "/agenda", label: "Agenda", short: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/belscript", label: "Belscript", short: "Script", icon: PhoneCall, testid: "nav-script", mobileHide: true },
  { to: "/antwoorden", label: "Antwoorden", short: "Antwoord", icon: MessagesSquare, testid: "nav-objections", mobileHide: true },
  { to: "/leads", label: "Leads", short: "Leads", icon: Users, testid: "nav-leads", queue: true },
  { to: "/kennisbank", label: "Diensten & kennisbank", short: "Kennis", icon: BookOpen, testid: "nav-kennisbank" },
  { to: "/vragen", label: "Vragen & suggesties", short: "Vragen", icon: MessageCircleQuestion, testid: "nav-vragen", badge: true },
  { to: "/beheer", label: "Beheer", short: "Beheer", icon: Settings, testid: "nav-beheer", adminOnly: true },
];

const visibleNav = (user) => NAV.filter((n) => !n.adminOnly || user?.role === "admin" || (n.to === "/beheer" && user?.permissions?.length));

function Sidebar() {
  const { user, logout } = useAuth();
  return (
    <aside className="hidden lg:flex w-[260px] shrink-0 border-r border-[#E2E7EC] bg-white flex-col fixed h-screen z-20">
      <div className="px-6 h-[76px] flex items-center gap-3 border-b border-[#E2E7EC]">
        <BrandLogo imgClass="max-h-10 max-w-[160px] object-contain" fallback={
          <>
            <div className="h-10 w-10 rounded-xl bg-[#001C51] flex items-center justify-center text-white shrink-0">
              <Building2 className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="leading-tight">
              <p className="font-heading font-bold text-[#16202B] text-lg">Flex Estate</p>
              <p className="text-xs text-[#5B6675]">CRM</p>
            </div>
          </>
        } />
        <NewLeadBell className="ml-auto" />
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {visibleNav(user).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 font-medium ${isActive ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}>
            <n.icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
            <span className="text-[15px]">{n.label}</span>
            {n.badge && <FeedbackBadge className="ml-auto" />}
            {n.queue && <CallQueueBadge className="ml-auto" />}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#E2E7EC]">
        <div className="px-3 py-2 mb-1">
          <p className="text-sm font-medium text-[#16202B] truncate">{user?.name}</p>
          <p className="text-xs text-[#5B6675] truncate">{user?.email}</p>
        </div>
        <ChangePassword variant="full" />
        <button data-testid="logout-btn" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#B0413E] hover:bg-[#F3DEDD] transition-colors font-medium">
          <LogOut className="h-5 w-5" strokeWidth={1.75} /> Uitloggen
        </button>
      </div>
    </aside>
  );
}

function MobileHeader() {
  const { logout } = useAuth();
  return (
    <header className="lg:hidden fixed top-0 inset-x-0 h-16 z-30 flex items-center gap-3 px-4 border-b border-[#E2E7EC] bg-white/80 backdrop-blur-xl">
      <BrandLogo imgClass="max-h-9 max-w-[160px] object-contain" fallback={
        <>
          <div className="h-9 w-9 rounded-xl bg-[#001C51] flex items-center justify-center text-white shrink-0">
            <Building2 className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div className="leading-tight">
            <p className="font-heading font-bold text-[#16202B] text-base">Flex Estate</p>
            <p className="text-[11px] text-[#5B6675] -mt-0.5">CRM</p>
          </div>
        </>
      } />
      <div className="ml-auto flex items-center gap-1">
        <NewLeadBell />
        <ChangePassword variant="icon" />
        <button data-testid="logout-btn-mobile" onClick={logout} className="p-2 rounded-lg text-[#B0413E] hover:bg-[#F3DEDD] transition-colors">
          <LogOut className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </div>
    </header>
  );
}

function BottomNav() {
  const { user } = useAuth();
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[#E2E7EC] bg-white/90 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-stretch justify-around">
        {visibleNav(user).filter((n) => !n.mobileHide).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottom-${n.testid}`}
            className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-1 py-2.5 min-h-[60px] transition-colors ${isActive ? "text-[#001C51]" : "text-[#8994A3]"}`}>
            {({ isActive }) => (
              <>
                <span className={`relative flex items-center justify-center h-8 w-8 rounded-xl transition-colors ${isActive ? "bg-[#E3E9F5]" : ""}`}>
                  <n.icon className="h-[22px] w-[22px]" strokeWidth={isActive ? 2 : 1.75} />
                  {n.badge && <FeedbackBadge className="absolute -top-1 -right-2" />}
                </span>
                <span className="text-[10px] font-medium leading-none">{n.short}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function Shell() {
  const { user } = useAuth();
  return (
    <BrowserRouter>
      <NewLeadsProvider>
      <Sidebar />
      <MobileHeader />
      <main className="lg:ml-[260px] min-h-screen pt-16 lg:pt-0 pb-24 lg:pb-0">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/belomgeving" element={<Belomgeving />} />
          <Route path="/belomgeving/:leadId" element={<Belomgeving />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/vragen" element={<Vragen />} />
          <Route path="/belscript" element={<LiveScript />} />
          <Route path="/antwoorden" element={<Objections />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/kennisbank" element={<Kennisbank />} />
          <Route path="/beheer" element={user?.role === "admin" || user?.permissions?.length ? <Beheer /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <BottomNav />
      <LiveCallAlert />
      </NewLeadsProvider>
    </BrowserRouter>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F9FB]">
        <Loader2 className="h-8 w-8 animate-spin text-[#001C51]" />
      </div>
    );
  }
  return user ? <Shell /> : <Auth />;
}

function App() {
  useEffect(() => { document.title = "Flex Estate · CRM"; }, []);
  return (
    <div className="App">
      <AuthProvider>
        <Gate />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
